/**
 * PDP Zoom Window
 * Shows a zoomed image in a window to the right of the gallery on desktop hover.
 * A lens overlay on the main image indicates the zoomed region.
 */
(function () {
  'use strict';

  var ZOOM_RATIO = 2.5;
  var HOVER_DELAY_MS = 300;
  var GAP = 12;
  var MIN_WINDOW_W = 200;

  var zoomResult = null;
  var lens = null;
  var activeContainer = null;
  var hoverTimer = null;
  var isActive = false;
  var latestEvent = null;

  /* ── Helpers ─────────────────────────────────────────────────── */

  function ensureZoomResult() {
    if (!zoomResult) {
      zoomResult = document.createElement('div');
      zoomResult.className = 'pdp-zoom-result';
      document.body.appendChild(zoomResult);
    }
  }

  function getHighResUrl(img) {
    var src = img.currentSrc || img.src || '';
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    try {
      var url = new URL(src);
      url.searchParams.delete('width');
      url.searchParams.set('width', '2400');
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  /* ── Core ────────────────────────────────────────────────────── */

  function activate(container, e) {
    ensureZoomResult();
    var img = container.querySelector('img');
    if (!img) return;

    var rect = container.getBoundingClientRect();
    var availRight = window.innerWidth - rect.right - GAP - 16;
    if (availRight < MIN_WINDOW_W) return;

    activeContainer = container;
    isActive = true;

    var imgSrc = getHighResUrl(img);
    var zoomW = Math.min(rect.width * 0.75, availRight);
    var zoomH = rect.height * 0.75;
    var bgW = rect.width * ZOOM_RATIO;
    var bgH = rect.height * ZOOM_RATIO;
    var lensW = zoomW / ZOOM_RATIO;
    var lensH = zoomH / ZOOM_RATIO;

    /* Create lens inside the hover container (position: relative) */
    lens = document.createElement('div');
    lens.className = 'pdp-zoom-lens';
    lens.style.width = lensW + 'px';
    lens.style.height = lensH + 'px';
    container.appendChild(lens);

    /* Position the zoom result window (fixed to viewport) */
    zoomResult.style.display = 'block';
    zoomResult.style.width = zoomW + 'px';
    zoomResult.style.height = zoomH + 'px';
    zoomResult.style.top = rect.top + 'px';
    zoomResult.style.left = (rect.right + GAP) + 'px';
    zoomResult.style.backgroundImage = 'url(' + imgSrc + ')';
    zoomResult.style.backgroundSize = bgW + 'px ' + bgH + 'px';

    /* Hide the cursor-following zoom tooltip */
    //window.dispatchEvent(new CustomEvent('zoom-controller-mouseleave'));

    moveLens(e);
  }

  function moveLens(e) {
    if (!isActive || !activeContainer || !lens || !zoomResult) return;
    if (!activeContainer.isConnected) { deactivate(); return; }

    var rect = activeContainer.getBoundingClientRect();
    var lensW = lens.offsetWidth;
    var lensH = lens.offsetHeight;

    /* Cursor position relative to container, clamped */
    var x = e.clientX - rect.left - lensW / 2;
    var y = e.clientY - rect.top - lensH / 2;
    x = Math.max(0, Math.min(x, rect.width - lensW));
    y = Math.max(0, Math.min(y, rect.height - lensH));

    lens.style.left = x + 'px';
    lens.style.top = y + 'px';

    /* Map lens position to zoom-window background-position (%) */
    var maxX = rect.width - lensW;
    var maxY = rect.height - lensH;
    var pctX = maxX > 0 ? (x / maxX) * 100 : 50;
    var pctY = maxY > 0 ? (y / maxY) * 100 : 50;
    zoomResult.style.backgroundPosition = pctX + '% ' + pctY + '%';
  }

  function deactivate() {
    if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
    isActive = false;
    activeContainer = null;
    latestEvent = null;
    if (lens && lens.parentNode) lens.parentNode.removeChild(lens);
    lens = null;
    if (zoomResult) zoomResult.style.display = 'none';
  }

  /* ── Global listeners ────────────────────────────────────────── */
  // window.addEventListener('scroll', function () { if (isActive) deactivate(); }, { passive: true });  
  window.addEventListener('scroll', function () {
    if (!isActive || !activeContainer || !zoomResult || !latestEvent) return;
    var rect = activeContainer.getBoundingClientRect();
    zoomResult.style.top = rect.top + 'px';
    zoomResult.style.left = (rect.right + GAP) + 'px';
    moveLens(latestEvent);
  }, { passive: true });
  window.addEventListener('resize', function () { if (isActive) deactivate(); }, { passive: true });
  window.addEventListener('zoom-opened', deactivate);

  /* ── Public API ──────────────────────────────────────────────── */

  /**
   * Bind zoom-window behaviour to all [data-pdp-hover-zoom] containers.
   * Safe to call repeatedly — already-bound containers are skipped.
   */
  window.pdpBindZoomWindow = function () {
    ensureZoomResult();
    document.querySelectorAll('[data-pdp-hover-zoom]').forEach(function (el) {
      if (el.dataset.zoomWindowBound === 'true') return;
      el.dataset.zoomWindowBound = 'true';

      el.addEventListener('mouseenter', function (e) {
        if (window.matchMedia('(max-width: 1023px)').matches) return;
        latestEvent = e;
        hoverTimer = setTimeout(function () {
          activate(el, latestEvent || e);
        }, HOVER_DELAY_MS);
      });

      el.addEventListener('mousemove', function (e) {
        latestEvent = e;
        if (isActive && activeContainer === el) moveLens(e);
      });

      el.addEventListener('mouseleave', deactivate);
    });
  };

  /** Force-close the zoom window (e.g. before slide DOM re-render). */
  window.pdpDeactivateZoom = deactivate;
})();
